import React, { useEffect, useState, useCallback, useRef } from 'react';
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from "use-places-autocomplete";
import { useLoadScript } from "@react-google-maps/api";
import { Input } from './ui/input';
import { MapPin } from 'lucide-react';

const LIBRARIES = ["places"];

// défini à la fin du fichier pour s'assurer que OSMAddressSearch et AddressAutocompleteInner sont hoistés
function AddressAutocompleteWrapper(props) {
    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: LIBRARIES,
    });

    // Google Maps disponible → version complète
    if (isLoaded && !loadError) return <AddressAutocompleteInner {...props} />;

    // Google Maps indisponible (quota, clé invalide, pas d'internet) → version OSM
    return <OSMAddressSearch {...props} />;
}

// ── Fallback complet OSM + Nominatim (aucune clé API) ────────────────────────
function OSMAddressSearch({ onLocationSelect, defaultValue = "", countryCode = "ht", regionContext = "", cityContext = "" }) {
    const [query,       setQuery]       = useState(defaultValue || (cityContext ? `${cityContext}${regionContext ? ', ' + regionContext : ''}` : ''));
    const [suggestions, setSuggestions] = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [selected,    setSelected]    = useState(null);   // { lat, lon }
    const debounceRef = useRef(null);

    const searchNominatim = async (q) => {
        if (!q || q.length < 3) { setSuggestions([]); return; }
        setLoading(true);
        try {
            const country = countryCode === 'ht' ? 'Haiti' : countryCode;
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q + ', ' + country)}&format=json&limit=6&addressdetails=1`,
                { headers: { 'Accept-Language': 'fr' } }
            );
            const data = await res.json();
            setSuggestions(data);
        } catch { setSuggestions([]); }
        finally { setLoading(false); }
    };

    const handleInput = (e) => {
        const val = e.target.value;
        setQuery(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchNominatim(val), 400);
    };

    const handleSelect = (place) => {
        const lat = parseFloat(place.lat);
        const lon = parseFloat(place.lon);
        setSelected({ lat, lon });
        setQuery(place.display_name);
        setSuggestions([]);
        if (onLocationSelect) {
            onLocationSelect({
                address: place.display_name,
                city:    place.address?.city || place.address?.town || place.address?.village || cityContext || '',
                region:  place.address?.state || regionContext || '',
                country: place.address?.country || '',
                zipCode: place.address?.postcode || '',
                coordinates: { lat, lon, lng: lon },
                placeId: null,
            });
        }
    };

    return (
        <div className="space-y-2">
            <div className="relative">
                <Input
                    value={query}
                    onChange={handleInput}
                    placeholder="Entrez votre rue ou lieu-dit…"
                    className="w-full h-11 pr-10"
                />
                {loading && (
                    <span className="absolute right-3 top-3 text-gray-400 text-xs">⏳</span>
                )}
                {suggestions.length > 0 && (
                    <ul className="absolute z-50 w-full bg-white mt-1 border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                        {suggestions.map((s, i) => (
                            <li key={i}
                                className="px-3 py-2.5 border-b last:border-0 hover:bg-orange-50 cursor-pointer text-sm"
                                onClick={() => handleSelect(s)}>
                                <span className="font-medium text-gray-900 line-clamp-1">{s.display_name}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Carte OSM */}
            <LeafletMap
                center={selected ? { lat: selected.lat, lng: selected.lon } : { lat: 18.5432, lng: -72.3395 }}
                marker={selected ? { lat: selected.lat, lng: selected.lon } : null}
                onMarkerDrop={(lat, lng) => {
                    setSelected({ lat, lon: lng });
                    // Géocodage inverse via Nominatim
                    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=fr`)
                        .then(r => r.json())
                        .then(data => {
                            const addr = data.address || {};
                            if (onLocationSelect) onLocationSelect({
                                address: data.display_name || `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                                city:    addr.city || addr.town || addr.village || cityContext || '',
                                region:  addr.state || regionContext || '',
                                country: addr.country || '',
                                zipCode: addr.postcode || '',
                                coordinates: { lat, lon: lng, lng },
                            });
                            setQuery(data.display_name || '');
                        })
                        .catch(() => {
                            if (onLocationSelect) onLocationSelect({
                                address: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                                city: cityContext || '', region: regionContext || '',
                                country: '', zipCode: '',
                                coordinates: { lat, lon: lng, lng },
                            });
                        });
                }}
            />
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                ⚠️ Mode hors ligne (Google Maps indisponible) — recherche via OpenStreetMap
            </p>
        </div>
    );
}

// ── Carte OpenStreetMap via Leaflet CDN (pas de clé API) ─────────────────────
function LeafletMap({ center, marker, onMarkerDrop }) {
    const divRef   = useRef(null);
    const mapRef   = useRef(null);
    const mkrRef   = useRef(null);

    // Charger Leaflet CSS + JS depuis CDN une seule fois
    useEffect(() => {
        if (!document.getElementById('leaflet-css')) {
            const css = document.createElement('link');
            css.id = 'leaflet-css';
            css.rel = 'stylesheet';
            css.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
            document.head.appendChild(css);
        }
        if (window.L) { _initMap(); return; }
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = _initMap;
        document.head.appendChild(script);

        return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function _initMap() {
        if (!divRef.current || mapRef.current) return;
        const L = window.L;
        const map = L.map(divRef.current).setView([center.lat, center.lng], 13);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OSM</a>'
        }).addTo(map);
        mapRef.current = map;

        if (marker) _addOrMoveMarker(marker.lat, marker.lng);

        map.on('click', (e) => { onMarkerDrop(e.latlng.lat, e.latlng.lng); });
        setTimeout(() => map.invalidateSize(), 100);
    }

    function _addOrMoveMarker(lat, lng) {
        if (!mapRef.current || !window.L) return;
        const L = window.L;
        if (mkrRef.current) {
            mkrRef.current.setLatLng([lat, lng]);
        } else {
            mkrRef.current = L.marker([lat, lng], { draggable: true }).addTo(mapRef.current);
            mkrRef.current.on('dragend', (e) => {
                const p = e.target.getLatLng();
                onMarkerDrop(p.lat, p.lng);
            });
        }
        mapRef.current.setView([lat, lng], 16);
    }

    // Mettre à jour le marqueur si les props changent
    useEffect(() => {
        if (!mapRef.current || !window.L) return;
        if (marker) _addOrMoveMarker(marker.lat, marker.lng);
        else mapRef.current.setView([center.lat, center.lng], 13);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marker, center]);

    return (
        <div>
            <div ref={divRef}
                style={{ width: '100%', height: '200px', borderRadius: '8px',
                         border: '1px solid #e5e7eb' }} />
            <p className="text-xs text-gray-400 text-center mt-1">
                Cliquez sur la carte pour placer/déplacer le repère · Glissez le repère pour affiner
            </p>
        </div>
    );
}

function AddressAutocompleteInner({
    onLocationSelect,
    defaultValue = "",
    className = "",
    placeholder = "Entrez votre rue ou lieu-dit...",
    countryCode = "ht",
    regionContext = "",
    cityContext = ""
}) {
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: {
            componentRestrictions: { country: countryCode }
        },
        debounce: 300,
        defaultValue: defaultValue,
    });

    // Reset suggestions if country changes
    useEffect(() => {
        clearSuggestions();
    }, [countryCode, clearSuggestions]);

    const [mapCenter, setMapCenter] = useState({ lat: 18.53917, lng: -72.335 }); // Port-au-Prince par défaut
    const [markerPosition, setMarkerPosition] = useState(null);
    const [initialGeocodeDone, setInitialGeocodeDone] = useState(false);
    const [isLocating, setIsLocating] = useState(false);

    // Context changes (City/Region from dropdowns)
    useEffect(() => {
        if (!ready) return;
        if (cityContext || regionContext) {
            const parts = [];
            if (cityContext) parts.push(cityContext);
            if (regionContext) parts.push(regionContext);
            const contextStr = parts.join(", ");

            // Only update value if the user hasn't typed a full address that already includes the city
            if (!value || !value.includes(cityContext)) {
                // Pre-fill the input with the city/region so user can just type their street before it
                setValue(contextStr, false);
            }

            // Recenter the map to the selected city/region
            getGeocode({ address: contextStr + ", " + countryCode })
                .then((results) => {
                    if (results[0]) {
                        const { lat, lng } = getLatLng(results[0]);
                        setMapCenter({ lat, lng });
                    }
                })
                .catch(err => console.log("Context Geocode Init Error:", err));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cityContext, regionContext, countryCode, ready, setValue]);

    // Initialisation
    useEffect(() => {
        if (ready && defaultValue && !value) {
            setValue(defaultValue, false);

            if (!initialGeocodeDone) {
                setInitialGeocodeDone(true);
                getGeocode({ address: defaultValue })
                    .then((results) => {
                        if (results[0]) {
                            const { lat, lng } = getLatLng(results[0]);
                            setMapCenter({ lat, lng });
                            setMarkerPosition({ lat, lng });
                        }
                    })
                    .catch(err => console.log("Geocode Init:", err));
            }
        }
    }, [defaultValue, ready, setValue, value, initialGeocodeDone]);

    const handleInput = (e) => {
        setValue(e.target.value);
        if (onLocationSelect) {
            onLocationSelect({
                address: e.target.value,
                city: "",
                country: "",
                coordinates: markerPosition ? { lat: markerPosition.lat, lon: markerPosition.lng, lng: markerPosition.lng } : null,
                placeId: null
            });
        }
    };

    const handleSelect = (suggestion) => () => {
        const { description } = suggestion;
        setValue(description, false);
        clearSuggestions();

        getGeocode({ address: description })
            .then((results) => {
                const fullAddress = results[0].formatted_address;
                const placeId = results[0].place_id;
                let city = "";
                let country = "";
                let region = "";
                let zipCode = "";

                results[0].address_components.forEach(component => {
                    if (component.types.includes("locality")) city = component.long_name;
                    if (component.types.includes("country")) country = component.long_name;
                    if (component.types.includes("administrative_area_level_1")) region = component.long_name;
                    if (component.types.includes("postal_code")) zipCode = component.long_name;
                });

                const { lat, lng } = getLatLng(results[0]);
                setMapCenter({ lat, lng });
                setMarkerPosition({ lat, lng });

                if (onLocationSelect) {
                    onLocationSelect({
                        address: fullAddress,
                        city: city,
                        region: region,
                        country: country,
                        zipCode: zipCode,
                        coordinates: { lat, lon: lng, lng },
                        placeId: placeId
                    });
                }
            })
            .catch((error) => {
                console.error("Error fetching coordinates: ", error);
                alert("Erreur de localisation Google API. S'il vous plaît, placez simplement le repère rouge MANUELLEMENT sur la carte en cliquant dessus.");
            });
    };

    const handleMapClick = useCallback((e) => {
        // Safe check for event type (Google Maps click event vs mocked event)
        let lat, lng;
        if (e && e.latLng) {
            lat = typeof e.latLng.lat === 'function' ? e.latLng.lat() : e.latLng.lat;
            lng = typeof e.latLng.lng === 'function' ? e.latLng.lng() : e.latLng.lng;
        } else {
            return; // Invalid event
        }

        setMarkerPosition({ lat, lng });

        // Immediately update the parent with coordinates to ensure they are saved,
        // even if the reverse geocoding API below fails or is disabled!
        if (onLocationSelect) {
            onLocationSelect({
                address: value || "", // keep current input value
                city: cityContext || "",
                region: regionContext || "",
                country: countryCode,
                zipCode: "",
                coordinates: { lat, lon: lng, lng },
                placeId: null
            });
        }

        getGeocode({ location: { lat, lng } })
            .then((results) => {
                if (results[0]) {
                    const fullAddress = results[0].formatted_address;
                    const placeId = results[0].place_id;
                    let city = "";
                    let country = "";
                    let region = "";
                    let zipCode = "";

                    results[0].address_components.forEach(component => {
                        if (component.types.includes("locality")) city = component.long_name;
                        if (component.types.includes("country")) country = component.long_name;
                        if (component.types.includes("administrative_area_level_1")) region = component.long_name;
                        if (component.types.includes("postal_code")) zipCode = component.long_name;
                    });

                    setValue(fullAddress, false);
                    clearSuggestions();

                    if (onLocationSelect) {
                        onLocationSelect({
                            address: fullAddress,
                            city: city,
                            region: region,
                            country: country,
                            zipCode: zipCode,
                            coordinates: { lat, lon: lng, lng },
                            placeId: placeId
                        });
                    }
                }
            })
            .catch((error) => {
                console.error("Reverse geocoding error: ", error);
                // We already saved the lat/lng at the top of the function, so it's fine if this fails!
            });
    }, [onLocationSelect, setValue, clearSuggestions, value, cityContext, regionContext, countryCode]);

    const handleGetCurrentLocation = useCallback(() => {
        if (!navigator.geolocation) {
            console.error("Geolocalisation non supportée");
            return;
        }

        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;

                setMapCenter({ lat, lng });
                setMarkerPosition({ lat, lng });

                // Immediately save coordinates in case reverse geocoding fails
                if (onLocationSelect) {
                    onLocationSelect({
                        address: value || "Ma Position",
                        city: cityContext || "",
                        region: regionContext || "",
                        country: countryCode,
                        zipCode: "",
                        coordinates: { lat, lon: lng, lng },
                        placeId: null
                    });
                }

                getGeocode({ location: { lat, lng } })
                    .then((results) => {
                        if (results[0]) {
                            const fullAddress = results[0].formatted_address;
                            const placeId = results[0].place_id;
                            let city = "";
                            let country = "";
                            let region = "";
                            let zipCode = "";

                            results[0].address_components.forEach(component => {
                                if (component.types.includes("locality")) city = component.long_name;
                                if (component.types.includes("country")) country = component.long_name;
                                if (component.types.includes("administrative_area_level_1")) region = component.long_name;
                                if (component.types.includes("postal_code")) zipCode = component.long_name;
                            });

                            setValue(fullAddress, false);
                            clearSuggestions();

                            if (onLocationSelect) {
                                onLocationSelect({
                                    address: fullAddress,
                                    city: city,
                                    region: region,
                                    country: country,
                                    zipCode: zipCode,
                                    coordinates: { lat, lon: lng, lng },
                                    placeId: placeId
                                });
                            }
                        }
                    })
                    .catch((error) => console.error("Reverse geocoding error: ", error))
                    .finally(() => setIsLocating(false));
            },
            (error) => {
                console.error("Erreur de position:", error);
                setIsLocating(false);
                alert("Impossible d'obtenir la position GPS. Assurez-vous d'avoir autorisé la localisation.");
            },
            { enableHighAccuracy: true }
        );
    }, [onLocationSelect, setValue, clearSuggestions, value, cityContext, regionContext, countryCode]);

    return (
        <div className={`relative ${className} w-full`}>
            <div className="relative">
                <Input
                    value={value}
                    onChange={handleInput}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            if (value) {
                                getGeocode({ address: value + (countryCode === 'ht' ? ', Haiti' : '') })
                                    .then((results) => {
                                        if (results[0]) {
                                            const { lat, lng } = getLatLng(results[0]);
                                            const eventMock = { latLng: { lat: () => lat, lng: () => lng } };
                                            handleMapClick(eventMock);
                                        }
                                    })
                                    .catch((error) => console.log("Manual search error: ", error));
                            }
                        }
                    }}
                    placeholder={placeholder}
                    className="w-full h-11 pr-12 border-gray-300 shadow-sm"
                />
                <button
                    type="button"
                    onClick={handleGetCurrentLocation}
                    disabled={isLocating}
                    title="Obtenir ma position actuelle"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-orange-500 hover:bg-orange-50 rounded-full transition-colors disabled:opacity-50"
                >
                    <MapPin className={`h-5 w-5 ${isLocating ? 'animate-pulse text-orange-500' : ''}`} />
                </button>
                {status === "OK" && (
                    <ul className="absolute z-50 w-full bg-white mt-1 border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                        {data.map((suggestion) => {
                            const {
                                place_id,
                                structured_formatting: { main_text, secondary_text },
                            } = suggestion;

                            return (
                                <li
                                    key={place_id}
                                    className="px-4 py-3 border-b last:border-0 hover:bg-orange-50 cursor-pointer text-sm text-gray-700 flex flex-col"
                                    onClick={handleSelect(suggestion)}
                                >
                                    <span className="font-semibold text-gray-900">{main_text}</span>
                                    <span className="text-gray-500 text-xs">{secondary_text}</span>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="mt-2">
                <LeafletMap
                    center={mapCenter}
                    marker={markerPosition}
                    onMarkerDrop={(lat, lng) => {
                        const eventMock = { latLng: { lat: () => lat, lng: () => lng } };
                        handleMapClick(eventMock);
                    }}
                />
            </div>
        </div >
    );
}

export default AddressAutocompleteWrapper;
